import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { PAGES } from './nav';
import CandidatesPanel from './CandidatesPanel';
import SectionHeader from './SectionHeader';
import RecentEvents from './RecentEvents';
import { useRangeBot } from '../../lib/useRangeBot';

const money = (v) => `${Number(v || 0) >= 0 ? '+' : ''}${Number(v || 0).toFixed(2)}$`;

function Tile({ label, value, sub, tone = 'slate', onClick, testId }) {
    const tones = {
        slate: 'border-slate-700 bg-slate-800/70', emerald: 'border-emerald-500/30 bg-emerald-500/5',
        rose: 'border-rose-500/30 bg-rose-500/5', amber: 'border-amber-500/30 bg-amber-500/5', sky: 'border-sky-500/30 bg-sky-500/5',
    };
    return (
        <button onClick={onClick} data-testid={testId}
                className={`text-left rounded-xl border px-3.5 py-3 transition-colors hover:border-slate-500 ${tones[tone]}`}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{label}</div>
            <div className="text-lg font-semibold text-slate-50 mt-0.5 font-mono tracking-tight">{value}</div>
            {sub && <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">{sub}</div>}
        </button>
    );
}

function BotTile({ label, mode, m, onClick, testId }) {
    if (!m) return <Tile label={label} value="—" sub="yükleniyor" onClick={onClick} testId={testId} />;
    const open = (m.open || []).length;
    const net = Number(m.today?.net ?? m.today?.realized ?? 0);
    return (
        <Tile
            label={label}
            value={m.enabled ? `${open} açık` : 'Kapalı'}
            sub={m.enabled ? `bugün ${money(net)} · ${mode === 'real' ? 'Binance' : 'sanal defter'}` : `${open ? `${open} açık pozisyon izleniyor · ` : ''}${mode === 'real' ? 'Binance' : 'sanal defter'}`}
            tone={m.enabled ? (net >= 0 ? 'emerald' : 'rose') : 'slate'}
            onClick={onClick}
            testId={testId}
        />
    );
}

/** Ana sayfa: botların özeti, radar, bugün ne yapmalı + tüm sayfalara hızlı erişim kartları. */
export default function HomeOverview({ ctx }) {
    const navigate = useNavigate();
    const rg = useRangeBot().status;
    const st = ctx.at?.status;
    const modes = st?.modes || {};
    const pump = st?.pump || {};
    const nCand = (pump.candidates || []).length;
    const nWarm = (pump.warm || []).length;
    const nSignals = (ctx.liveRows || []).filter((r) => r.verdict && !['BEKLE', 'WAIT', 'KOVALAMA'].includes(r.verdict.action)).length;
    return (
        <div className="space-y-5" data-testid="home-overview">
            <div className="rounded-2xl border border-slate-700 bg-slate-800/60 px-4 sm:px-6 py-4 sm:py-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300">Ana Sayfa</div>
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-50 mt-1">Bugün ne oluyor?</h1>
                <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-3xl">
                    Botların durumu, radar ve bugün yapılacaklar tek bakışta. Her kart ilgili sayfayı açar; üst menüden başlıklara göre de gezebilirsin.
                </p>
                <div className="grid grid-cols-2 lg:grid-cols-6 gap-2.5 mt-4">
                    <BotTile label="🏦 Gerçek bot" mode="real" m={modes.real} onClick={() => navigate('/bot')} testId="home-tile-real" />
                    <BotTile label="🧪 Sanal bot" mode="paper" m={modes.paper} onClick={() => navigate('/bot')} testId="home-tile-paper" />
                    <Tile label="📐 Aralık Botu" value={!rg ? '—' : rg.enabled ? `${(rg.coins || []).filter((c) => c.open).length} pozisyonda` : 'Kapalı'}
                          sub={!rg ? 'yükleniyor' : `${(rg.coins || []).length} coin · bugün ${money(rg.today?.net_usd)} · ${rg.mode === 'real' ? 'Binance' : 'sanal defter'}`}
                          tone={rg?.enabled ? (Number(rg.today?.net_usd || 0) >= 0 ? 'emerald' : 'rose') : 'slate'} onClick={() => navigate('/aralik-bot')} testId="home-tile-range" />
                    <Tile label="⚡💥 Radar" value={`${nCand} aday`} sub={`${nWarm} ısınıyor · ${pump.scanned || 0} coin taranıyor`} tone={nCand ? 'amber' : 'slate'} onClick={() => navigate('/bot')} testId="home-tile-radar" />
                    <Tile label="🎯 Sinyaller" value={`${nSignals} karar`} sub={`${(ctx.liveRows || []).length} coin taramada · ${ctx.alerts?.length || 0} erken uyarı`} tone={nSignals ? 'sky' : 'slate'} onClick={() => navigate('/sinyaller')} testId="home-tile-signals" />
                    <Tile label="⭐ Favoriler" value={`${(ctx.watchlist || []).length}`} sub="yıldızladığın coinler canlı" onClick={() => navigate('/favoriler')} testId="home-tile-favorites" />
                </div>
            </div>

            <SectionHeader
                id="adaylar"
                emoji="🔥"
                title="Bugün ne yapmalı — kısa liste"
                subtitle="Patlama adayları + sistem önerileri, botun ne yaptığıyla. Tam liste ve diğer coinler için Sinyaller menüsü."
                right={<button onClick={() => navigate('/sinyaller')} className="text-[11px] text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1" data-testid="home-goto-signals">Tümü <ArrowRight className="h-3 w-3" /></button>}
            />
            <CandidatesPanel rows={ctx.liveRows} onSelect={ctx.selectCoin} tickPrices={ctx.tickPrices} botStatus={st} />

            <RecentEvents onSelect={ctx.selectCoin} />

            <SectionHeader id="sayfalar" emoji="🗂" title="Sayfalar" subtitle="Üst menüdeki her sayfa burada da listelenir — tıkla, tek başına açılsın." />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5" data-testid="home-page-cards">
                {PAGES.filter((p) => p.path !== '/').map((p) => (
                    <button key={p.path} onClick={() => navigate(p.path)} data-testid={`home-card-${p.path.slice(1)}`}
                            className="text-left rounded-xl border border-slate-700 bg-slate-800/50 hover:bg-slate-800 hover:border-slate-500 px-3.5 py-3 transition-colors">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{p.group.label}</div>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-base leading-none">{p.emoji}</span>
                            <span className="text-sm font-semibold text-slate-50">{p.label}</span>
                        </div>
                        <div className="text-[11px] text-slate-400 mt-1 leading-snug">{p.desc}</div>
                    </button>
                ))}
            </div>
        </div>
    );
}
